import React, { useState, useEffect, useMemo } from 'react';
import { IconData, IconType } from '../../../types/pageBuilder';
import { iconsService } from '../../../services/iconsService';
import { X, Search, Upload, Loader } from 'lucide-react';

interface IconSelectorProps {
  selectedIconId: number | null;
  onSelect: (iconId: number, iconData: IconData) => void;
  onClose: () => void;
  onUpload?: (iconData: FormData) => Promise<number>;
}

/**
 * Visual icon picker with search and categories
 */
const IconSelector: React.FC<IconSelectorProps> = ({
  selectedIconId,
  onSelect,
  onClose,
  onUpload,
}) => {
  const [icons, setIcons] = useState<Record<string, IconData[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<IconType | 'all'>('all');

  // Load icons on mount
  useEffect(() => {
    loadIcons();
  }, []);

  const loadIcons = async () => {
    try {
      setLoading(true);
      setError(null);
      const groupedIcons = await iconsService.getIconsByCategory();
      setIcons(groupedIcons);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load icons');
    } finally {
      setLoading(false);
    }
  };

  // Get all categories
  const categories = useMemo(() => {
    return ['all', ...Object.keys(icons)];
  }, [icons]);

  // Filter icons based on search and filters
  const filteredIcons = useMemo(() => {
    let allIcons: IconData[] = [];

    if (activeCategory === 'all') {
      allIcons = Object.values(icons).flat();
    } else {
      allIcons = icons[activeCategory] || [];
    }

    // Apply type filter
    if (typeFilter !== 'all') {
      allIcons = allIcons.filter((icon) => icon.type === typeFilter);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      allIcons = allIcons.filter(
        (icon) =>
          icon.name.toLowerCase().includes(query) ||
          icon.display_name.toLowerCase().includes(query)
      );
    }

    return allIcons;
  }, [icons, activeCategory, typeFilter, searchQuery]);

  // Render icon based on type
  const renderIcon = (icon: IconData) => {
    switch (icon.type) {
      case 'svg':
        return (
          <div
            className="w-8 h-8 flex items-center justify-center"
            dangerouslySetInnerHTML={{ __html: icon.code }}
          />
        );
      case 'emoji':
        return <span className="text-3xl">{icon.code}</span>;
      case 'fontawesome':
        return <i className={`${icon.code} text-2xl`} />;
      case 'image':
        return <img src={icon.code} alt={icon.display_name} className="w-8 h-8 object-contain" />;
      default:
        return null;
    }
  };

  const handleIconSelect = (icon: IconData) => {
    onSelect(icon.id, icon);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Select Icon</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Search and Filters */}
        <div className="p-4 border-b space-y-3">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search icons..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                  activeCategory === category
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>

          {/* Type Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Type:</span>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as IconType | 'all')}
              className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="svg">SVG</option>
              <option value="emoji">Emoji</option>
              <option value="fontawesome">Font Awesome</option>
              <option value="image">Image</option>
            </select>

            {onUpload && (
              <button
                onClick={() => {
                  // Trigger file upload
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'image/svg+xml';
                  input.onchange = async (e) => {
                    const file = (e.target as HTMLInputElement).files?.[0];
                    if (file) {
                      const formData = new FormData();
                      formData.append('icon', file);
                      try {
                        await onUpload(formData);
                        await loadIcons(); // Reload icons
                      } catch (err) {
                        setError(err instanceof Error ? err.message : 'Failed to upload icon');
                      }
                    }
                  };
                  input.click();
                }}
                className="ml-auto px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
              >
                <Upload size={16} />
                Upload SVG
              </button>
            )}
          </div>
        </div>

        {/* Icons Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="animate-spin text-blue-500" size={32} />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600">{error}</p>
              <button
                onClick={loadIcons}
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Retry
              </button>
            </div>
          ) : filteredIcons.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No icons found</p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-2 text-blue-500 hover:text-blue-600"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-3">
              {filteredIcons.map((icon) => (
                <button
                  key={icon.id}
                  onClick={() => handleIconSelect(icon)}
                  className={`
                    relative p-4 rounded-lg border-2 transition-all
                    ${
                      selectedIconId === icon.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                    }
                  `}
                  title={icon.display_name}
                >
                  <div className="flex items-center justify-center">{renderIcon(icon)}</div>
                  <p className="text-xs text-gray-600 mt-2 truncate text-center">{icon.name}</p>
                  {selectedIconId === icon.id && (
                    <div className="absolute top-1 right-1 w-3 h-3 bg-blue-500 rounded-full" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50">
          <p className="text-sm text-gray-600">
            {filteredIcons.length} icon{filteredIcons.length !== 1 ? 's' : ''} available
          </p>
        </div>
      </div>
    </div>
  );
};

export default IconSelector;
